import {
  type BillingDetail,
  type ContainerMetrics,
  type EmbeddedMap,
  type Feedback,
  type ModalState,
  ModalType,
  type Nav,
  NoticeType,
  type ResourceStats,
  type Role,
  type U2fDevice,
} from "@app/types";
import { createSchema, slice } from "starfx";
import * as factory from "./factory";

export const [schema, initialState] = createSchema({
  cache: slice.table(),
  loaders: slice.loaders<any>(),
  env: slice.obj(factory.defaultConfig()),
  feedback: slice.obj<Feedback>({
    preDeploySurveyAnswered: false,
    freeformFeedbackGiven: false,
  }),
  users: slice.table({ empty: factory.defaultUser() }),
  token: slice.obj(factory.defaultToken()),
  elevatedToken: slice.obj(factory.defaultToken()),
  activityLogsToken: slice.str(),
  invitations: slice.table({ empty: factory.defaultInvitation() }),
  entities: slice.table<EmbeddedMap>(),
  redirectPath: slice.str(),
  scimToken: slice.str(),
  organizationSelected: slice.str(),
  organizations: slice.table({ empty: factory.defaultOrganization() }),
  u2fDevices: slice.table<U2fDevice>(),
  otp: slice.obj(factory.defaultOtp()),
  theme: slice.str("light"),
  nav: slice.obj<Nav>({
    collapsed: false,
  }),
  modal: slice.obj<ModalState>({ type: ModalType.NONE, props: {} }),
  roles: slice.table<Role>({ empty: factory.defaultRole() }),
  currentUserRoles: slice.any<string[]>([]),
  signal: slice.any(new AbortController()),
  resourceStats: slice.table<ResourceStats>(),
  containerMetrics: slice.table<ContainerMetrics>(),
  billingDetail: slice.obj<BillingDetail>(factory.defaultBillingDetail()),
  apps: slice.table({ empty: factory.defaultDeployApp() }),
  appConfigs: slice.table({ empty: factory.defaultDeployAppConfig() }),
  certificates: slice.table({ empty: factory.defaultDeployCertificate() }),
  endpoints: slice.table({ empty: factory.defaultDeployEndpoint() }),
  environments: slice.table({ empty: factory.defaultDeployEnvironment() }),
  environmentStats: slice.table({
    empty: factory.defaultDeployEnvironmentStats(),
  }),
  serviceDefinitions: slice.table({
    empty: factory.defaultDeployServiceDefinition(),
  }),
  stacks: slice.table({ empty: factory.defaultDeployStack() }),
  disks: slice.table({ empty: factory.defaultDeployDisk() }),
  // Persistent disks are a separate deploy-api model from `disks` (database
  // volumes): standalone disks that attach to app services.
  persistentDisks: slice.table({
    empty: factory.defaultDeployPersistentDisk(),
  }),
  diskAttachments: slice.table({
    empty: factory.defaultDeployDiskAttachment(),
  }),
  databases: slice.table({ empty: factory.defaultDeployDatabase() }),
  databaseCredentials: slice.table({
    empty: factory.defaultDatabaseCredential(),
  }),
  databaseImages: slice.table({ empty: factory.defaultDeployDatabaseImage() }),
  services: slice.table({ empty: factory.defaultDeployService() }),
  logDrains: slice.table({ empty: factory.defaultDeployLogDrain() }),
  metricDrains: slice.table({ empty: factory.defaultDeployMetricDrain() }),
  operations: slice.table({ empty: factory.defaultDeployOperation() }),
  activePlans: slice.table({ empty: factory.defaultActivePlan() }),
  llmGatewayConfigurations: slice.table({
    empty: factory.defaultDeployLlmGatewayConfiguration(),
  }),
  llmGatewaySelfServiceEnrollment: slice.any<boolean>(true),
  llmGatewayUsage: slice.table({ empty: factory.defaultLlmGatewayUsage() }),
  llmGatewayRequests: slice.table({
    empty: factory.defaultLlmGatewayRequest(),
  }),
  llmGatewayRequestPages: slice.table({
    empty: factory.defaultLlmGatewayRequestPage(),
  }),
  llmGatewayKeys: slice.table({
    empty: factory.defaultDeployLlmGatewayKey(),
  }),
  llmKeys: slice.table({ empty: factory.defaultDeployLlmKey() }),
  llmKeyRequests: slice.table({ empty: factory.defaultDeployLlmKeyRequest() }),
  llmPolicies: slice.table({ empty: factory.defaultDeployLlmPolicy() }),
  llmTraceDrains: slice.table({ empty: factory.defaultDeployLlmTraceDrain() }),
  plans: slice.table({ empty: factory.defaultPlan() }),
  permissions: slice.table({ empty: factory.defaultPermission() }),
  releases: slice.table({ empty: factory.defaultDeployRelease() }),
  containers: slice.table({ empty: factory.defaultDeployContainer() }),
  vpcPeers: slice.table({ empty: factory.defaultDeployVpcPeer() }),
  vpnTunnels: slice.table({ empty: factory.defaultDeployVpnTunnel() }),
  edges: slice.table({ empty: factory.defaultDeployEdge() }),
  backups: slice.table({ empty: factory.defaultDeployBackup() }),
  backupRps: slice.table({ empty: factory.defaultBackupRp() }),
  images: slice.table({ empty: factory.defaultDeployImage() }),
  memberships: slice.table({ empty: factory.defaultMembership() }),
  serviceSizingPolicies: slice.table({
    empty: factory.defaultServiceSizingPolicy(),
  }),
  // v2 (scheduled HAS) — unreleased
  serviceSizingPolicySchedulesV2: slice.table({
    empty: factory.defaultServiceSizingPolicyScheduleV2(),
  }),
  deployments: slice.table({ empty: factory.defaultDeployment() }),
  sources: slice.table({ empty: factory.defaultDeploySource() }),
  settings: slice.table({ empty: factory.defaultDeploySetting() }),
  maintenanceWindows: slice.table({
    empty: factory.defaultDeployMaintenanceWindow(),
  }),
  manualScaleRecommendations: slice.table({
    empty: factory.defaultManualScaleRecommendation(),
  }),
  notices: slice.obj<Record<NoticeType, string>>({
    [NoticeType.NONE]: "",
  }),
  costs: slice.table({
    empty: factory.defaultCost(),
  }),
  costRates: slice.obj(factory.defaultCostRates()),
  dashboards: slice.table({ empty: factory.defaultDeployDashboard() }),
  externalAwsAccounts: slice.table({
    empty: factory.defaultDeployExternalAwsAccount(),
  }),
  externalAwsResources: slice.table({
    empty: factory.defaultDeployExternalAwsResource(),
  }),
  appExternalAwsRdsConnections: slice.table({
    empty: factory.defaultDeployAppExternalAwsRdsConnection(),
  }),
  globalControls: slice.table({ empty: factory.defaultGlobalControl() }),
  globalControlFrameworks: slice.table({
    empty: factory.defaultGlobalControlFramework(),
  }),
  customerControls: slice.table({
    empty: factory.defaultDeployCustomerControl(),
  }),
  customerControlResources: slice.table({
    empty: factory.defaultDeployCustomerControlResource(),
  }),
  userAgreements: slice.table({ empty: factory.defaultUserAgreement() }),
});
export type WebState = typeof initialState;
